<template>
  <div
    class="w-full h-screen flex items-center justify-center bg-gray-100 px-4"
  >
    <div
      class="max-w-md w-full bg-[var(--whiteBG)] rounded-2xl shadow-lg p-8 text-center"
    >
      <!-- Icon -->
      <div class="flex justify-center mb-6">
        <div
          class="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center"
        >
          <svg
            class="w-10 h-10 text-red-500"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M18.364 5.636a9 9 0 010 12.728M15.536 8.464a5 5 0 010 7.072M12 12h.01M8.464 8.464a5 5 0 000 7.072M5.636 5.636a9 9 0 000 12.728"
            />
          </svg>
        </div>
      </div>

      <!-- Title -->
      <h2 class="text-xl font-semibold text-gray-800 mb-2">Connection Lost</h2>

      <!-- Description -->
      <p class="text-sm text-gray-500 mb-3">
        Your internet connection appears to be unstable or disconnected.
      </p>

      <!-- Status -->
      <div class="mb-4">
        <span
          class="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium"
          :class="
            isOnline ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
          "
        >
          <span
            class="w-2 h-2 rounded-full"
            :class="isOnline ? 'bg-green-500' : 'bg-red-500'"
          ></span>
          {{ isOnline ? "Back Online" : "Offline" }}
        </span>
      </div>

      <!-- Speed -->
      <p v-if="speedResult" class="text-xs text-blue-600 mb-2">
        Speed: {{ speedResult.speedKBps.toFixed(0) }} KB/s ({{
          speedResult.speedMbps.toFixed(2)
        }}
        Mbps)
      </p>

      <!-- Checking -->
      <p v-if="isTesting" class="text-xs text-yellow-600 mb-2">
        Checking network speed...
      </p>

      <!-- Countdown -->
      <p class="text-xs text-gray-400 mb-6">
        Auto retrying in {{ countdown }} seconds...
      </p>

      <!-- Actions -->
      <div class="flex flex-col gap-3">
        <button
          @click="manualRetry"
          :disabled="isTesting"
          class="w-full py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
        >
          {{ isTesting ? "Testing..." : "Retry Now" }}
        </button>

        <button
          @click="refreshPage"
          class="w-full py-2.5 border border-gray-300 text-gray-600 text-sm rounded-lg hover:bg-gray-50 transition"
        >
          Reload Page
        </button>
      </div>

      <p class="text-xs text-gray-400 mt-6">
        If the issue persists, contact your administrator.
      </p>
    </div>
  </div>
</template>

<script>
export default {
  name: "NetworkDisconnectPage",

  data() {
    return {
      isOnline: navigator.onLine,

      retryDelay: 5000,
      maxDelay: 30000,

      retryTimeout: null,
      countdownInterval: null,

      countdown: 5,
      speedResult: null,
      isTesting: false,
    };
  },

  mounted() {
    window.addEventListener("online", this.handleOnline);
    window.addEventListener("offline", this.handleOffline);

    if (!this.isOnline) {
      this.startRetryCycle();
    }
  },

  beforeUnmount() {
    window.removeEventListener("online", this.handleOnline);
    window.removeEventListener("offline", this.handleOffline);

    clearTimeout(this.retryTimeout);
    clearInterval(this.countdownInterval);
  },

  methods: {
    handleOffline() {
      this.isOnline = false;
      this.startRetryCycle();
    },

    async handleOnline() {
      this.isOnline = true;

      clearTimeout(this.retryTimeout);
      clearInterval(this.countdownInterval);

      await this.checkSpeedAndProceed();
    },

    // Retry cycle with countdown
    startRetryCycle() {
      clearTimeout(this.retryTimeout);
      clearInterval(this.countdownInterval);

      this.countdown = this.retryDelay / 1000;

      this.countdownInterval = setInterval(() => {
        if (this.countdown > 0) this.countdown--;
      }, 1000);

      this.retryTimeout = setTimeout(async () => {
        clearInterval(this.countdownInterval);

        try {
          const result = await this.testNetworkSpeed();
          this.speedResult = result;
        } catch {
          console.warn("error");
        }

        if (navigator.onLine) {
          await this.checkSpeedAndProceed();
        } else {
          this.retryDelay = Math.min(this.retryDelay * 2, this.maxDelay);
          this.startRetryCycle();
        }
      }, this.retryDelay);
    },

    // Speed test
    async testNetworkSpeed() {
      const testFile =
        "https://upload.wikimedia.org/wikipedia/commons/3/3f/Fronalpstock_big.jpg";

      const start = Date.now();

      const res = await fetch(testFile + "?t=" + start, {
        cache: "no-store",
      });

      const blob = await res.blob();
      const end = Date.now();

      const duration = (end - start) / 1000;
      const sizeKB = blob.size / 1024;

      const speedKBps = sizeKB / duration;
      const speedMbps = (speedKBps * 8) / 1024;

      return { speedKBps, speedMbps };
    },

    //
    // Check speed and decide
    async checkSpeedAndProceed() {
      try {
        this.isTesting = true;

        const result = await this.testNetworkSpeed();
        this.speedResult = result;

        this.isTesting = false;

        if (result.speedKBps >= 500) {
          setTimeout(async () => {
            localStorage.setItem("network_speed", JSON.stringify(result));
            await this.syncAndRedirect();
          }, 1500);
        } else {
          alert(
            `Connected but slow (${result.speedKBps.toFixed(
              0
            )} KB/s). Retrying...`
          );

          this.startRetryCycle();
        }
      } catch {
        this.isTesting = false;
        this.startRetryCycle();
      }
    },

    async manualRetry() {
      await this.checkSpeedAndProceed();
    },

    async syncAnswers() {
      return new Promise((res) => setTimeout(res, 1000));
    },

    async syncAndRedirect() {
      try {
        await this.syncAnswers();

        this.retryDelay = 5000;

        const lastPage = localStorage.getItem("last_visited_page");

        if (lastPage) {
          localStorage.removeItem("last_visited_page");
          this.$router.replace(lastPage);
        } else {
          this.$router.replace("/");
        }
      } catch {
        this.startRetryCycle();
      }
    },

    refreshPage() {
      window.location.reload();
    },
  },
};
</script>
